import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { fetchUsers, deleteUser } from '../customers';
import HistoryModal from './HistoryModal';

const PAGE_SIZE = 5;
const COLUMNS = [
  { key: 'firstName', label: 'CUSTOMER' },
  { key: 'contact', label: 'PHONE' },
  { key: 'billAmount', label: 'BILLING' },
];

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyUser, setHistoryUser] = useState(null);
  const [servicePopup, setServicePopup] = useState(null);
  const [notePopup, setNotePopup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // user to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('ppt_auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/users/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers_export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export customers to Excel.');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchUsers({ page, limit: PAGE_SIZE, search, sortBy, order });
      setUsers(data.users);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch {
      setError('Could not load customer records. Please make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, order]);

  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  function handleSearchChange(e) {
    setPage(1);
    setSearch(e.target.value);
  }

  function toggleSort(key) {
    setPage(1);
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('asc');
    }
  }

  const totalBillingSum = users.reduce((acc, curr) => acc + (Number(curr.billAmount) || 0), 0);
  const totalVisitsCount = users.reduce((acc, curr) => acc + (curr.serviceHistory || []).length, 0);

  return (
    <div className="space-y-6 animate-rise relative z-10">
      {/* Title & Primary Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-primary m-0">Customer Records</h1>
          <p className="text-xs md:text-sm text-txt-muted m-0 mt-1 font-medium">
            Real-time management of client directory, service treatments &amp; billing receipts.
          </p>
        </div>

        <Link
          className="inline-flex items-center gap-2 bg-cta hover:bg-cta-hover text-white font-extrabold text-xs md:text-sm px-5 py-2.5 rounded transition-all no-underline shadow-md hover:shadow-lg border-0 shrink-0 cursor-pointer"
          to="/admin/create-user"
        >
          <span>+ Add Customer Record</span>
        </Link>
      </div>

      {/* 3 Compact Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {/* Card 1: Total Customers */}
        <div className="p-3.5 rounded bg-white/80 backdrop-blur-2xl border-2 border-accent/40 shadow-xs hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between group cursor-pointer">
          <div>
            <span className="text-[10px] font-extrabold text-txt-muted uppercase tracking-wider block">Total Customers</span>
            <b className="font-heading text-2xl font-bold text-primary block leading-tight">{total}</b>
            <span className="text-[10px] text-accent-muted font-bold">Verified Directory</span>
          </div>
          <span className="w-11 h-11 rounded bg-accent-soft border border-accent/30 grid place-items-center shrink-0 shadow-2xs group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
            <svg className="w-6 h-6 text-cta" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          </span>
        </div>

        {/* Card 2: Total Revenue */}
        <div className="p-3.5 rounded bg-white/80 backdrop-blur-2xl border-2 border-cta/40 shadow-xs hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between group cursor-pointer">
          <div>
            <span className="text-[10px] font-extrabold text-txt-muted uppercase tracking-wider block">Total Revenue</span>
            <b className="font-heading text-2xl font-bold text-cta block leading-tight">₹{totalBillingSum.toLocaleString()}</b>
            <span className="text-[10px] text-cta font-bold">Collected Revenue</span>
          </div>
          <span className="w-11 h-11 rounded bg-accent-soft border border-accent/30 grid place-items-center shrink-0 shadow-2xs group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
            <svg className="w-6 h-6 text-cta" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
          </span>
        </div>

        {/* Card 3: Completed Visits */}
        <div className="p-3.5 rounded bg-white/80 backdrop-blur-2xl border-2 border-accent/40 shadow-xs hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between group cursor-pointer">
          <div>
            <span className="text-[10px] font-extrabold text-txt-muted uppercase tracking-wider block">Completed Visits</span>
            <b className="font-heading text-2xl font-bold text-primary block leading-tight">{totalVisitsCount}</b>
            <span className="text-[10px] text-txt-muted font-bold">ISO Audited Logs</span>
          </div>
          <span className="w-11 h-11 rounded bg-accent-soft border border-accent/30 grid place-items-center shrink-0 shadow-2xs group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
            <svg className="w-6 h-6 text-cta" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
            </svg>
          </span>
        </div>
      </div>

      {/* Search Bar & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Search Input with Static Purple Border */}
        <div className="relative flex-1 max-w-md border-2 border-accent/40 focus-within:border-cta rounded bg-white shadow-xs transition-colors">
          <input
            className="w-full pl-10 pr-9 py-2.5 rounded bg-white text-xs md:text-sm text-slate-900 font-semibold placeholder-slate-400 outline-none focus:ring-2 focus:ring-cta/20 transition-all border-0"
            type="text"
            placeholder="Search customer name, email or phone hotline..."
            value={search}
            onChange={handleSearchChange}
          />
          <svg className="w-4 h-4 text-cta absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 font-bold text-xs transition-colors cursor-pointer"
              title="Clear Search Filter"
            >
              ✕
            </button>
          )}
        </div>

        {/* Status Badge Pod & Export */}
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold transition-all shadow-md disabled:opacity-70 disabled:cursor-wait cursor-pointer"
          >
            {exporting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 384 512">
                  <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM155.7 250.2L192 302.3l36.3-52.1c7.6-10.9 22.6-13.5 33.5-5.9s13.5 22.6 5.9 33.5L221.3 344l46.4 66.2c7.6 10.9 5 25.8-5.9 33.5s-25.8 5-33.5-5.9L192 385.7l-36.3 52.1c-7.6 10.9-22.6 13.5-33.5 5.9s-13.5-22.6-5.9-33.5L162.7 344l-46.4-66.2c-7.6-10.9-5-25.8 5.9-33.5s25.8-5 33.5 5.9z" />
                </svg>
                Export to Excel
              </>
            )}
          </button>
          <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold shrink-0">
            <span>
              {total > 0 ? (
                <>
                  Showing <b className="text-slate-900 font-bold">{users.length}</b> of <b className="text-slate-900 font-bold">{total}</b> records
                </>
              ) : (
                <b className="text-slate-900 font-bold">0 records found</b>
              )}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded text-xs font-semibold shadow-xs flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white/70 backdrop-blur-xl border border-white/80 rounded p-16 text-center text-txt-muted text-xs font-semibold animate-pulse shadow-sm">
          Loading customer records...
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white/80 backdrop-blur-2xl border border-white rounded p-12 text-center shadow-sm space-y-4 animate-rise">
          <div className="w-14 h-14 rounded bg-accent-soft border border-accent/20 text-accent-muted grid place-items-center mx-auto shadow-2xs">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
          </div>
          <div>
            <strong className="block font-heading text-base font-bold text-primary">No Customer Records Found</strong>
            <p className="text-xs text-txt-muted max-w-sm mx-auto mt-1 font-medium leading-relaxed">
              We couldn't find any customer matching "{search}". Try searching by another name, email address, or phone hotline.
            </p>
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="bg-cta hover:bg-cta-hover text-white font-extrabold text-xs px-5 py-2.5 rounded transition-all shadow-md hover:shadow-lg border-0 cursor-pointer"
            >
              Reset Search Filter
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Apple Glass Data Table */}
          <div className="bg-white/80 backdrop-blur-xl rounded border border-white/90 shadow-sm">
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full border-collapse text-left text-xs md:text-sm">
                <thead>
                  <tr className="bg-primary text-white text-[11px] uppercase tracking-widest">
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="py-3 px-5 font-extrabold cursor-pointer select-none hover:text-accent-bright transition-colors whitespace-nowrap"
                        onClick={() => toggleSort(col.key)}
                      >
                        <div className="flex items-center gap-1.5">
                          {col.label}
                          <span className="text-white/50 text-[10px]">
                            {sortBy === col.key ? (order === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className="py-2.5 px-5 font-extrabold whitespace-nowrap">TREATED SERVICES</th>
                    <th className="py-2.5 px-5 font-extrabold">NOTES</th>
                    <th className="py-2.5 px-5 font-extrabold text-center">VISITS</th>
                    <th className="py-2.5 px-5 font-extrabold text-right">ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u, idx) => {
                    const visitCount = (u.serviceHistory || []).length;
                    return (
                      <tr
                        key={u._id}
                        className={`transition-all duration-150 group cursor-default ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                          } hover:bg-emerald-50/70 hover:shadow-[inset_3px_0_0_0_theme(colors.emerald.500)]`}
                      >
                        <td className="p-4 px-5">
                          <div>
                            <strong className="block font-heading font-extrabold text-slate-900 group-hover:text-primary transition-colors text-sm leading-tight capitalize">
                              {String(u.firstName || '').toLowerCase()} {String(u.lastName || '').toLowerCase()}
                            </strong>
                            <span className="block text-[11px] text-slate-400 font-medium tracking-wide mt-0.5">{u.email}</span>
                          </div>
                        </td>
                        <td className="p-4 px-5">
                          <span className="text-slate-800 font-semibold text-sm">{u.contact}</span>
                        </td>
                        <td className="p-4 px-5">
                          <span className="inline-flex items-center gap-1 font-black text-cta text-sm">
                            ₹{Number(u.billAmount || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="p-4 px-5 max-w-[220px]">
                          <div className="flex flex-wrap gap-1.5">
                            {(!u.services || u.services.length === 0) ? (
                              <span className="text-slate-300 text-xs">—</span>
                            ) : (
                              <>
                                {u.services.slice(0, 1).map((s) => (
                                  <span
                                    key={s}
                                    className="bg-accent/10 text-accent-muted text-[10px] font-bold px-2 py-1 rounded border border-accent/20 truncate max-w-[160px]"
                                  >
                                    {s}
                                  </span>
                                ))}
                                {u.services.length > 1 && (
                                  <span
                                    className="bg-primary/8 text-primary text-[10px] font-bold px-2 py-1 rounded border border-primary/20 cursor-pointer inline-block hover:bg-primary/15 transition-colors"
                                    onMouseEnter={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setServicePopup({ services: u.services.slice(1), x: rect.left, y: rect.top });
                                    }}
                                    onMouseLeave={() => setServicePopup(null)}
                                  >
                                    +{u.services.length - 1} more
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="p-4 px-5 max-w-[180px]">
                          {u.notes ? (
                            <span
                              className="text-slate-500 font-medium text-xs truncate block cursor-help"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setNotePopup({ text: u.notes, x: rect.left, y: rect.top });
                              }}
                              onMouseLeave={() => setNotePopup(null)}
                            >
                              {u.notes}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="p-4 px-5 text-center">
                          <span className="w-7 h-7 rounded bg-primary/10 text-primary text-xs font-extrabold inline-grid place-items-center border border-primary/20">
                            {visitCount}
                          </span>
                        </td>
                        <td className="p-4 px-5 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            {visitCount > 0 && (
                              <button
                                className="bg-white hover:bg-primary hover:text-white border border-primary/30 hover:border-primary text-primary font-bold p-2 rounded transition-all cursor-pointer inline-flex items-center justify-center shadow-sm group/btn hover:shadow-md"
                                title="View Service History"
                                onClick={() => setHistoryUser(u)}
                              >
                                <svg className="w-5 h-5 transition-transform group-hover/btn:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            )}
                            <button
                              className="bg-white hover:bg-red-500 hover:text-white border border-red-200 hover:border-red-500 text-red-500 font-bold p-2 rounded transition-all cursor-pointer inline-flex items-center justify-center shadow-sm group/del hover:shadow-md"
                              title="Delete Record"
                              onClick={() => setDeleteTarget(u)}
                            >
                              <svg className="w-5 h-5 transition-transform group-hover/del:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-white/70 backdrop-blur-xl border border-white/80 rounded shadow-2xs">
            <span className="text-xs font-bold text-txt-muted">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="bg-white/80 border border-white/90 px-4 py-2 rounded text-xs font-bold text-primary hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                ← Previous
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="bg-white/80 border border-white/90 px-4 py-2 rounded text-xs font-bold text-primary hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <HistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />

      {/* Services popup rendered at document.body level via portal — bypasses all backdrop-blur stacking contexts */}
      {servicePopup && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: servicePopup.x, top: servicePopup.y - 8, transform: 'translateY(-100%)' }}
        >
          <div className="bg-white border border-slate-200 rounded shadow-2xl p-3 min-w-[160px] max-w-[240px]">
            <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">All Services</p>
            <div className="flex flex-col gap-1.5">
              {servicePopup.services.map((s) => (
                <span key={s} className="bg-accent/10 text-accent-muted text-[10px] font-bold px-2 py-1 rounded border border-accent/20 block">
                  {s}
                </span>
              ))}
            </div>
            <div className="absolute top-full left-4 w-2.5 h-2.5 bg-white border-r border-b border-slate-200 rotate-45 -mt-1.5" />
          </div>
        </div>,
        document.body
      )}

      {notePopup && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: notePopup.x, top: notePopup.y - 8, transform: 'translateY(-100%)' }}
        >
          <div className="bg-white border border-slate-200 rounded shadow-2xl px-3 py-2.5 max-w-[280px]">
            <p className="text-[11px] font-medium leading-relaxed text-slate-700">{notePopup.text}</p>
            <div className="absolute top-full left-4 w-2.5 h-2.5 bg-white border-r border-b border-slate-200 rotate-45 -mt-1.5" />
          </div>
        </div>,
        document.body
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && createPortal(
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-[9999] p-4"
          onClick={() => !deleteLoading && setDeleteTarget(null)}
        >
          <div
            className="bg-white w-full max-w-md shadow-2xl border border-slate-200 rounded overflow-hidden animate-rise"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-red-100 grid place-items-center shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h2 className="font-heading font-bold text-slate-900 text-base m-0">Delete Customer Record</h2>
                <p className="text-[11px] text-slate-500 font-medium m-0 mt-0.5">This action cannot be undone</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-slate-700 font-medium leading-relaxed">
                Are you sure you want to permanently delete the record for{' '}
                <strong className="text-slate-900 capitalize">{String(deleteTarget.firstName || '').toLowerCase()} {String(deleteTarget.lastName || '').toLowerCase()}</strong>?
              </p>
              <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded text-[11px] text-red-600 font-semibold">
                ⚠️ This will also delete all <strong>{(deleteTarget.serviceHistory || []).length} service history</strong> records associated with this customer.
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button
                className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-xs font-bold text-white bg-red-500 hover:bg-red-600 border border-red-500 rounded transition-colors cursor-pointer disabled:opacity-60 inline-flex items-center gap-2"
                disabled={deleteLoading}
                onClick={async () => {
                  setDeleteLoading(true);
                  try {
                    await deleteUser(deleteTarget._id);
                    setDeleteTarget(null);
                    load(); // refresh the table
                  } catch (err) {
                    alert(err.message);
                  } finally {
                    setDeleteLoading(false);
                  }
                }}
              >
                {deleteLoading ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Deleting…
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
